import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const LoginCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processAuth = async () => {
      try {
        // Get auth data from URL params
        const authParam = searchParams.get('auth');

        if (!authParam) {
          throw new Error('No authentication data provided');
        }

        // Decode the auth data
        const authData = JSON.parse(atob(authParam));

        // Validate auth data
        if (!authData.accessToken || !authData.idToken) {
          throw new Error('Invalid authentication data');
        }

        // Check if auth data is not expired (5 minute window)
        const timestamp = authData.timestamp || 0;
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        if (now - timestamp > fiveMinutes) {
          throw new Error('Authentication data has expired. Please login again.');
        }

        // Store tokens in localStorage for the platform to use
        localStorage.setItem('niwt_access_token', authData.accessToken);
        localStorage.setItem('niwt_id_token', authData.idToken);
        if (authData.refreshToken) {
          localStorage.setItem('niwt_refresh_token', authData.refreshToken);
        }
        localStorage.setItem('niwt_user_email', authData.email);

        // Decode the ID token to get user attributes
        try {
          const idTokenPayload = JSON.parse(atob(authData.idToken.split('.')[1]));
          if (idTokenPayload) {
            // Store user attributes for profile display
            localStorage.setItem('niwt_user_attributes', JSON.stringify({
              email: idTokenPayload.email,
              given_name: idTokenPayload.given_name,
              family_name: idTokenPayload.family_name,
              'custom:company': idTokenPayload['custom:company'] || idTokenPayload['custom:organization'],
              'custom:role': idTokenPayload['custom:role'] || 'User',
              'custom:title': idTokenPayload['custom:title'],
              'custom:phone': idTokenPayload['custom:phone']
            }));
          }
        } catch (e) {
          console.error('Failed to decode user attributes:', e);
        }

        // Mark user as authenticated
        localStorage.setItem('niwt_authenticated', 'true');

        // Check for special redirect flags
        const setupMFA = searchParams.get('setup') === 'mfa';
        const isNewUser = searchParams.get('newUser') === 'true';

        // Redirect immediately without showing success screen
        if (setupMFA) {
          window.location.href = '/profile?tab=security';
        } else if (isNewUser) {
          window.location.href = '/welcome';
        } else {
          const homeRoute = localStorage.getItem('homeRoute');
          const validRoutes = ['/dashboard', '/chat'];
          window.location.href = (homeRoute && validRoutes.includes(homeRoute)) ? homeRoute : '/dashboard';
        }

      } catch (error: any) {
        console.error('Login callback error:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to complete login');
      }
    };

    processAuth();
  }, [searchParams, navigate]);

  const handleReturnToLogin = () => {
    // Clear any partial auth state
    localStorage.removeItem('niwt_access_token');
    localStorage.removeItem('niwt_id_token');
    localStorage.removeItem('niwt_refresh_token');
    localStorage.removeItem('niwt_user_email');
    localStorage.removeItem('niwt_authenticated');

    // Redirect back to platform login
    window.location.href = 'https://platform.niwt.com/';
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-purple-700 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Completing Login</h2>
              <p className="text-gray-600 mb-6">
                Please wait while we securely log you in...
              </p>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-purple-700 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-purple-700 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-purple-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Successful!</h2>
            <p className="text-gray-600 mb-6">
              Welcome! Redirecting you to your workspace...
            </p>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
              <p className="text-sm text-green-800">
                ✓ Authentication complete<br />
                ✓ Loading your workspace...
              </p>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Failed</h2>
          <p className="text-gray-600 mb-4">
            We couldn't complete your login.
          </p>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
            <p className="text-sm text-red-800">
              {errorMessage}
            </p>
          </div>
          <button
            onClick={handleReturnToLogin}
            className="w-full bg-purple-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginCallback;